<!-- C:\MyVueApps\sentinel-safe\src\components\DocumentUploader.vue -->
<template>
  <div class="space-y-4">
    <div>
      <label class="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">{{ label }}</label>
      
      <!-- Drag & Drop Zone -->
      <div 
        class="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-all duration-300"
        :class="[
          isDragging 
            ? 'border-brand-accent bg-brand-accent/5 scale-[1.01] shadow-md' 
            : 'border-gray-300 bg-gray-50 hover:border-gray-400'
        ]"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <div class="space-y-2 text-center">
          <!-- Document Icon -->
          <svg 
            class="mx-auto h-12 w-12 transition-colors duration-300" 
            :class="isDragging ? 'text-brand-accent' : 'text-gray-400'"
            stroke="currentColor" fill="none" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>

          <div class="flex flex-col items-center text-sm text-gray-600">
            <!-- Brand Styled Mint Button -->
            <label :for="id" class="relative cursor-pointer bg-brand-accent text-gray-900 px-6 py-2 rounded-lg font-bold shadow-sm hover:bg-brand-primary hover:text-white transition-all duration-300 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-brand-accent mb-2">
              <span>Upload Documents</span>
              <input :id="id" :name="id" type="file" class="sr-only" multiple @change="handleFileChange" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,text/plain">
            </label>
            <p class="font-medium">or drag and drop files here</p>
          </div>
          <p class="text-xs text-gray-500 font-medium uppercase tracking-tight">PDF, DOC, XLS, CSV up to {{ maxSize }}MB each</p>
        </div>
      </div>
    </div>

    <!-- Selected Files List -->
    <div v-if="selectedFiles.length > 0" class="space-y-3">
       <h3 class="text-sm font-bold text-gray-700 flex items-center">
         <span class="bg-brand-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] mr-2">
           {{ selectedFiles.length }}
         </span>
         Selected Documents:
       </h3>
       <div v-for="(file, index) in selectedFiles" :key="index" class="flex items-center justify-between p-3 bg-white rounded-lg border-2 border-gray-100 shadow-sm hover:border-brand-accent transition-colors group">
        <div class="flex items-center space-x-3 overflow-hidden">
            <!-- Mini File Icon -->
            <svg class="h-6 w-6 text-brand-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <div class="flex flex-col min-w-0">
              <span class="text-sm font-semibold text-gray-800 truncate">{{ file.name }}</span>
              <span class="text-xs text-gray-400 uppercase font-bold">{{ (file.size / 1024 / 1024).toFixed(2) }} MB</span>
            </div>
        </div>
        <button @click="removeFile(index)" type="button" class="p-1.5 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all" title="Remove file">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
        </button>
       </div>
    </div>

    <!-- Error Messaging -->
    <p v-if="errorMessage" class="text-sm text-red-600 bg-red-50 p-2 rounded-md border border-red-100 font-medium">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const props = defineProps({
  id: {
    type: String,
    default: 'doc-upload'
  },
  label: {
    type: String,
    default: 'Upload Documents'
  },
  maxSize: { // in MB
    type: Number,
    default: 5
  }
});

const emit = defineEmits(['update:files']);

const selectedFiles = ref([]);
const errorMessage = ref('');
const isDragging = ref(false);

/**
 * Handle Drag and Drop events
 */
const onDragOver = () => {
  isDragging.value = true;
};

const onDragLeave = () => {
  isDragging.value = false;
};

const onDrop = (event) => {
  isDragging.value = false;
  const files = event.dataTransfer.files;
  if (files) {
    processFiles(files);
  }
};

/**
 * Handle File Selection from Browser Dialog
 */
const handleFileChange = (event) => {
  const files = event.target.files;
  if (files) {
    processFiles(files);
  }
  // Clear input so same file can be selected again if removed
  event.target.value = '';
};

/**
 * Core validation and file list management
 */
const processFiles = (fileList) => {
  errorMessage.value = '';
  const filesArray = Array.from(fileList);

  for (const file of filesArray) {
    // 1. Validate File Size
    if (file.size > props.maxSize * 1024 * 1024) {
      errorMessage.value = `File "${file.name}" exceeds the ${props.maxSize}MB limit.`;
      continue;
    }
    
    // 2. Validate File Extension (Basic check)
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt'];
    const fileName = file.name.toLowerCase();
    const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!isValidExtension) {
        errorMessage.value = `File "${file.name}" is not a supported document type.`;
        continue;
    }

    // 3. Add to unique list
    if (!selectedFiles.value.some(existing => existing.name === file.name && existing.size === file.size)) {
        selectedFiles.value.push(file);
    }
  }
  
  emitUpdate();
};

const removeFile = (index) => {
  selectedFiles.value.splice(index, 1);
  emitUpdate();
};

const emitUpdate = () => {
  emit('update:files', selectedFiles.value);
};
</script>

<style scoped>
/* Project Brand Consistency */
.text-brand-accent { color: #20FCF1; }
.bg-brand-accent { background-color: #20FCF1; }
.border-brand-accent { border-color: #20FCF1; }
.focus\:ring-brand-accent:focus { --tw-ring-color: #20FCF1; }

/* Animation for drag feedback */
.scale-\[1\.01\] {
  transform: scale(1.01);
}
</style>